import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { Box, MenuItem, Select } from '@mui/material'
import { usePersistedState } from '../../hooks/usePersistedState'
import { curatorStudentsApiSlice } from '../../features/curator/curatorStudents'
import MonthWeekFilter from '../../components/monthWeekFilter/MonthWeekFilter'
import { filterField, filterLabel } from '../../components/filterSelect/FilterSelect'
import { capitalize } from '../../utils/functions'
import DirectorSubjectFilter from '../director/DirectorSubjectFilter'

export interface HeadMentor {
    mentorId: number
    name: string
    groupIds: number[]
}

// Группа -> её менторы и лек. У группы бывает несколько менторов: ученик считается у каждого.
export const useHeadIndex = () => {
    const { data: rows = [], isFetching } = curatorStudentsApiSlice.useFetchHeadMentorsQuery()
    return useMemo(() => {
        const mentors = new Map<number, HeadMentor>()
        const groupMentors = new Map<number, number[]>()
        const groupCourse = new Map<number, number>()
        const groupTitle = new Map<number, string>()
        rows.forEach((r) => {
            const m = mentors.get(r.mentorId) ?? {
                mentorId: r.mentorId,
                name: `${r.firstName} ${r.lastName}`.trim() || `#${r.mentorId}`,
                groupIds: [],
            }
            if (!m.groupIds.includes(r.groupId)) m.groupIds.push(r.groupId)
            mentors.set(r.mentorId, m)
            groupMentors.set(r.groupId, [...(groupMentors.get(r.groupId) ?? []), r.mentorId])
            groupCourse.set(r.groupId, r.courseId)
            groupTitle.set(r.groupId, r.groupTitle)
        })
        const mentorNames = (groupId?: number | null) =>
            (groupMentors.get(groupId ?? 0) ?? []).map((id) => mentors.get(id)?.name).filter(Boolean).join(', ')
        return { mentors: Array.from(mentors.values()), groupMentors, groupCourse, groupTitle, mentorNames, isFetching }
    }, [rows, isFetching])
}

export type HeadIndex = ReturnType<typeof useHeadIndex>

// Фильтры кабинета басшы: лек, «топ · ментор», ай/апта. Ключи общие со страницами ментора,
// поэтому выбор сохраняется при переходе между страницами.
export const useHeadFilters = () => {
    const [courseId, setCourseId] = usePersistedState<number | null>('curator.courseId', null)
    const [groupId, setGroupId] = usePersistedState<number | null>('curator.groupId', null)
    const [monthNumber, setMonthNumber] = usePersistedState<number | undefined>('curator.monthNumber', undefined)
    const [weekNumber, setWeekNumber] = usePersistedState<number | undefined>('curator.weekNumber', undefined)
    return { courseId, setCourseId, groupId, setGroupId, monthNumber, setMonthNumber, weekNumber, setWeekNumber }
}

export type HeadFiltersState = ReturnType<typeof useHeadFilters>

// Подходит ли группа под выбранные лек и группу
export const groupInScope = (index: HeadIndex, f: HeadFiltersState, groupId?: number | null) => {
    if (!groupId) return false
    if (f.groupId && f.groupId !== groupId) return false
    if (f.courseId && index.groupCourse.get(groupId) !== f.courseId) return false
    return true
}

export const HeadFilters: React.FC<{ filters: HeadFiltersState; index: HeadIndex; showPeriod?: boolean; showGroup?: boolean; children?: React.ReactNode }> = ({
    filters: f,
    index,
    showPeriod = true,
    showGroup = true,
    children,
}) => {
    const { formatMessage, locale } = useIntl()
    const { data: courseResponse } = curatorStudentsApiSlice.useFetchCuratorCoursesQuery()
    const courses = courseResponse?.data ?? []

    const groups = Array.from(index.groupTitle.entries())
        .filter(([id]) => !f.courseId || index.groupCourse.get(id) === f.courseId)
        .map(([id, title]) => ({ id, label: `${title} · ${index.mentorNames(id)}` }))
        .sort((a, b) => a.label.localeCompare(b.label))

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end', marginBottom: '20px' }}>
            <Box>
                <Box sx={filterLabel}>{formatMessage({ id: 'headLek' })}</Box>
                <Select
                    size="small"
                    displayEmpty
                    value={f.courseId ?? ''}
                    onChange={(e) => {
                        f.setCourseId(Number(e.target.value) || null)
                        f.setGroupId(null)
                    }}
                    sx={{ ...filterField, minWidth: 220 }}
                >
                    <MenuItem value="">{formatMessage({ id: 'headAllLeks' })}</MenuItem>
                    {courses.map((c) => (
                        <MenuItem key={c.id} value={c.id}>{c[`title${capitalize(locale)}`] || c.titleRu}</MenuItem>
                    ))}
                </Select>
            </Box>
            <DirectorSubjectFilter withLabel filters={f} />
            {showGroup && <Box>
                <Box sx={filterLabel}>{formatMessage({ id: 'headGroupMentor' })}</Box>
                <Select
                    size="small"
                    displayEmpty
                    value={f.groupId ?? ''}
                    onChange={(e) => f.setGroupId(Number(e.target.value) || null)}
                    sx={{ ...filterField, minWidth: 320 }}
                >
                    <MenuItem value="">{formatMessage({ id: 'allGroups' })}</MenuItem>
                    {groups.map((g) => (
                        <MenuItem key={g.id} value={g.id}>{g.label}</MenuItem>
                    ))}
                </Select>
            </Box>}
            {children}
            {showPeriod && (
                <MonthWeekFilter
                    courseId={f.courseId}
                    monthNumber={f.monthNumber}
                    weekNumber={f.weekNumber}
                    onChange={(m, w) => {
                        f.setMonthNumber(m)
                        f.setWeekNumber(w)
                    }}
                />
            )}
        </Box>
    )
}
